import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, List, ListItem, ListItemText, Chip, Typography, Box,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

/**
 * Dialog que mostra o resultado de sincronização por nó DNS.
 * Use após qualquer operação DNS que retorne {nodes: [{name, result, message?}]}.
 *
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   nodes      Array<{name, result, message?}>
 *   title      título do dialog (default: "Sincronização DNS")
 *   summary    string opcional acima da lista (ex.: "5 domínios removidos")
 */
export default function NodeSyncResult({ open, onClose, nodes = [], title = "Sincronização DNS", summary = null }) {
    const ok = nodes.filter(n => n.result === true || n.result === 1).length;
    const fail = nodes.length - ok;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                {summary && (
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {summary}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip
                        icon={<CheckCircleIcon />}
                        color="success"
                        size="small"
                        label={`${ok} sincronizado(s)`}
                    />
                    {fail > 0 && (
                        <Chip
                            icon={<CancelIcon />}
                            color="error"
                            size="small"
                            label={`${fail} com falha`}
                        />
                    )}
                </Box>

                {nodes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Nenhum nó DNS configurado. As alterações foram salvas no banco mas
                        nenhum servidor DNS foi atualizado.
                    </Typography>
                ) : (
                    <List dense sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
                        {nodes.map((n, i) => {
                            const success = n.result === true || n.result === 1;
                            return (
                                <ListItem key={`${n.name || 'node'}-${i}`} sx={{ py: 0.5 }}>
                                    {success
                                        ? <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                        : <CancelIcon color="error" sx={{ mr: 1 }} />}
                                    <ListItemText
                                        primary={n.name || `Nó ${i + 1}`}
                                        secondary={n.message || (success ? 'Sincronizado' : 'Falhou ao sincronizar')}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}
